import { subMonths, eachDayOfInterval, isWithinInterval, parseISO, format } from 'date-fns';
import { Transaction } from '@/types/transaction';

export interface TimeSeriesPoint {
  date: string;
  value: number;
}

export interface SpendingPattern {
  dailyAverages: Record<string, number>;
  weeklyPattern: Record<number, number>;
  monthlyTrend: number;
  volatility: number;
  lastUpdated: string;
}

export function analyzeSpendingPattern(
  transactions: Transaction[],
  categoryId: string,
  months: number = 12
): SpendingPattern {
  const now = new Date();
  const startDate = subMonths(now, months);
  
  // Filter relevant transactions
  const categoryTransactions = transactions
    .filter(tx => tx.category?.id === categoryId)
    .filter(tx => {
      const txDate = new Date(tx.date);
      return isWithinInterval(txDate, { start: startDate, end: now });
    });

  // Calculate daily totals
  const dailyTotals: Record<string, number> = {};
  categoryTransactions.forEach(tx => {
    const date = format(new Date(tx.date), 'yyyy-MM-dd');
    dailyTotals[date] = (dailyTotals[date] || 0) + Math.abs(tx.amount);
  });

  // Calculate weekly pattern (0-6 for Sunday-Saturday)
  const weeklyPattern: Record<number, number> = Array(7).fill(0).reduce((acc, _, i) => ({
    ...acc,
    [i]: 0
  }), {});
  
  categoryTransactions.forEach(tx => {
    const day = new Date(tx.date).getDay();
    weeklyPattern[day] = (weeklyPattern[day] || 0) + Math.abs(tx.amount);
  });

  // Normalize weekly pattern to percentages
  const totalWeekly = Object.values(weeklyPattern).reduce((sum, val) => sum + val, 0);
  if (totalWeekly > 0) {
    Object.keys(weeklyPattern).forEach(day => {
      weeklyPattern[parseInt(day)] = (weeklyPattern[parseInt(day)] / totalWeekly) * 100;
    });
  }

  // Calculate monthly trend (simple linear regression)
  const monthlyTrend = calculateMonthlyTrend(categoryTransactions, months);

  // Calculate volatility (standard deviation of daily spends)
  const dailyValues = Object.values(dailyTotals);
  const volatility = calculateVolatility(dailyValues);

  return {
    dailyAverages: dailyTotals,
    weeklyPattern,
    monthlyTrend,
    volatility,
    lastUpdated: now.toISOString()
  };
}

function calculateMonthlyTrend(transactions: Transaction[], months: number): number {
  if (transactions.length < 2) return 0;
  
  // Group by month
  const monthlyTotals: Record<string, number> = {};
  const monthCounts: Record<string, number> = {};
  
  transactions.forEach(tx => {
    const month = format(new Date(tx.date), 'yyyy-MM');
    monthlyTotals[month] = (monthlyTotals[month] || 0) + Math.abs(tx.amount);
    monthCounts[month] = (monthCounts[month] || 0) + 1;
  });
  
  // Calculate average per month
  const monthlyAverages = Object.entries(monthlyTotals).map(([month, total]) => ({
    month,
    average: total / (monthCounts[month] || 1)
  })).sort((a, b) => a.month.localeCompare(b.month));
  
  // Simple linear regression for trend
  if (monthlyAverages.length < 2) return 0;
  
  const x = monthlyAverages.map((_, i) => i);
  const y = monthlyAverages.map(avg => avg.average);
  
  return calculateLinearTrend(x, y);
}

function calculateLinearTrend(x: number[], y: number[]): number {
  const n = x.length;
  const xSum = x.reduce((sum, val) => sum + val, 0);
  const ySum = y.reduce((sum, val) => sum + val, 0);
  const xySum = x.reduce((sum, val, i) => sum + val * y[i], 0);
  const xSquaredSum = x.reduce((sum, val) => sum + val * val, 0);
  
  const slope = (n * xySum - xSum * ySum) / (n * xSquaredSum - xSum * xSum);
  return isFinite(slope) ? slope * 100 : 0; // Convert to percentage
}

function calculateVolatility(values: number[]): number {
  if (values.length < 2) return 0;
  
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / (values.length - 1);
  
  return Math.sqrt(variance);
}
